import { Stars02 } from '@untitledui/icons';
import { type ReactNode, useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { BadgeWithIcon } from '@/lib/base/badges/badges';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { logger } from '@/lib/logger';
// `entitySanitizeSchema` + `preprocessEntityTags` come from `entitySanitize`
// instead of `entityRendering` to avoid the EntityBadgeById → registry →
// section import cycle. See lib/markdown/entitySanitize.ts for the full
// explanation.
import { createEntityMarkdownComponents } from '@/lib/markdown/entityRendering';
import {
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entitySanitize';

import type { AISummary, HighlightType } from '../utils/meetingPrepTypes';
import { VALID_HIGHLIGHT_TYPES } from '../utils/meetingPrepTypes';
import { MeetingPrepAccordionSection } from './meeting-prep-accordion-section';

const REHYPE_PLUGINS = [
  rehypeRaw,
  [rehypeSanitize, entitySanitizeSchema],
] as unknown[];

// Keep in sync with HighlightType in ../utils/meetingPrepTypes.ts
const HIGHLIGHT_STYLES: Record<
  HighlightType,
  { containerClass: string; prefixClass: string; label: string }
> = {
  overdue: {
    containerClass:
      'border-l-2 border-utility-error-500 bg-utility-error-50 rounded-r-lg p-3',
    prefixClass: 'font-semibold text-utility-error-700',
    label: 'Overdue:',
  },
  new_attendee: {
    containerClass:
      'border-l-2 border-utility-blue-500 bg-utility-blue-50 rounded-r-lg p-3',
    prefixClass: 'font-semibold text-utility-blue-700',
    label: 'New attendee:',
  },
  risk: {
    containerClass:
      'border-l-2 border-utility-error-500 bg-utility-error-50 rounded-r-lg p-3',
    prefixClass: 'font-semibold text-utility-error-700',
    label: 'Risk:',
  },
  info: {
    containerClass:
      'border-l-2 border-utility-purple-500 bg-utility-purple-50 rounded-r-lg p-3',
    prefixClass: 'font-semibold text-utility-purple-700',
    label: 'Info:',
  },
};

const AI_BADGE = (
  <BadgeWithIcon iconLeading={Stars02} color='purple' size='sm'>
    AI
  </BadgeWithIcon>
);

interface MeetingPrepSummarySectionProps {
  aiSummary: AISummary | null;
  defaultExpanded: boolean;
  metadata?: string;
  badges?: ReactNode;
  orgId: number;
}

export function MeetingPrepSummarySection({
  aiSummary,
  defaultExpanded,
  metadata,
  badges,
  orgId,
}: MeetingPrepSummarySectionProps) {
  const markdownComponents = useMemo(
    () => createEntityMarkdownComponents(orgId),
    [orgId]
  );

  const filteredHighlights = useMemo(() => {
    if (!aiSummary) {
      return [];
    }
    const counts = new Map<string, number>();
    return aiSummary.highlights
      .filter(h => {
        if (VALID_HIGHLIGHT_TYPES.includes(h.type)) {
          return true;
        }
        logger.warn('Unknown highlight type', {
          component: 'crm_events',
          highlight_type: h.type,
        });
        return false;
      })
      .map(h => {
        const base = `${h.type}-${h.text}`;
        const count = counts.get(base) ?? 0;
        counts.set(base, count + 1);
        return { ...h, stableKey: count > 0 ? `${base}-${count}` : base };
      });
  }, [aiSummary]);

  return (
    <MeetingPrepAccordionSection
      title='Meeting prep'
      defaultExpanded={defaultExpanded}
      badges={
        <>
          {AI_BADGE}
          {badges}
        </>
      }
      metadata={metadata}
      sectionId='summary'
    >
      <div className='space-y-4'>
        {aiSummary && (
          <>
            <div className='text-sm text-[var(--color-text-secondary)]'>
              <MarkdownContent
                content={preprocessEntityTags(aiSummary.preamble)}
                size='compact'
                rehypePlugins={REHYPE_PLUGINS}
                components={markdownComponents}
                className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
              />
            </div>
            {filteredHighlights.length > 0 && (
              <div className='space-y-2'>
                {filteredHighlights.map(highlight => {
                  const style = HIGHLIGHT_STYLES[highlight.type];
                  return (
                    <div
                      key={highlight.stableKey}
                      className={style.containerClass}
                    >
                      <span className={style.prefixClass}>{style.label}</span>{' '}
                      <span className='text-sm inline'>
                        <MarkdownContent
                          content={preprocessEntityTags(highlight.text)}
                          size='compact'
                          rehypePlugins={REHYPE_PLUGINS}
                          components={markdownComponents}
                          className='inline [&>p]:inline'
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {aiSummary.focus_areas.length > 0 && (
              <div>
                <p className='text-xs font-medium text-[var(--color-text-tertiary)] mb-2'>
                  Focus areas
                </p>
                <ul className='list-disc list-inside space-y-1'>
                  {aiSummary.focus_areas.map(area => (
                    <li
                      key={area}
                      className='text-sm text-[var(--color-text-secondary)]'
                    >
                      <MarkdownContent
                        content={preprocessEntityTags(area)}
                        size='compact'
                        rehypePlugins={REHYPE_PLUGINS}
                        components={markdownComponents}
                        className='inline [&>p]:inline'
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </MeetingPrepAccordionSection>
  );
}
